import * as THREE from "three";

const cache = new Map();

const rng = (seed) => () => {
  seed = (seed * 16807) % 2147483647;
  return (seed - 1) / 2147483646;
};

const jitter = (hex, rnd, amt = 18) => {
  const n = parseInt(hex.slice(1), 16);
  const f = (v) => Math.max(0, Math.min(255, v + Math.round((rnd() - 0.5) * amt * 2)));
  return `rgb(${f(n >> 16)},${f((n >> 8) & 255)},${f(n & 255)})`;
};

// Touches de pinceau impressionnistes dans une zone
const dabs = (ctx, rnd, { x, y, w, h, colors, n, len = 14, width = 5, angle = 0, spread = 0.5 }) => {
  ctx.lineCap = "round";
  for (let i = 0; i < n; i += 1) {
    const px = x + rnd() * w;
    const py = y + rnd() * h;
    const a = angle + (rnd() - 0.5) * spread;
    const l = len * (0.5 + rnd());
    ctx.strokeStyle = jitter(colors[Math.floor(rnd() * colors.length)], rnd);
    ctx.globalAlpha = 0.55 + rnd() * 0.45;
    ctx.lineWidth = width * (0.6 + rnd() * 0.8);
    ctx.beginPath();
    ctx.moveTo(px, py);
    ctx.lineTo(px + Math.cos(a) * l, py + Math.sin(a) * l);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
};

const title = (ctx, text, x, y, size, color, font = "700 {s}px Helvetica, Arial, sans-serif", spacing = 0.18) => {
  ctx.fillStyle = color;
  ctx.font = font.replace("{s}", size);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  if ("letterSpacing" in ctx) ctx.letterSpacing = `${Math.round(size * spacing)}px`;
  ctx.fillText(text, x, y);
  if ("letterSpacing" in ctx) ctx.letterSpacing = "0px";
};

const ART = {
  // Monet — Impression, soleil levant
  soleil: [640, 460, (c, r, W, H) => {
    c.fillStyle = "#6F8C93"; c.fillRect(0, 0, W, H);
    dabs(c, r, { x: 0, y: 0, w: W, h: H * 0.55, colors: ["#8FA9AA", "#A8B7AE", "#C9A98F", "#7D9AA0", "#B9C4BB"], n: 1600, len: 22, width: 7, spread: 0.3 });
    dabs(c, r, { x: 0, y: H * 0.5, w: W, h: H * 0.5, colors: ["#4F6E78", "#5E7F86", "#3F5B66", "#7C989C"], n: 1700, len: 26, width: 6, spread: 0.15 });
    c.fillStyle = "#E8662E"; c.beginPath(); c.arc(W * 0.42, H * 0.36, 26, 0, Math.PI * 2); c.fill();
    dabs(c, r, { x: W * 0.36, y: H * 0.52, w: W * 0.12, h: H * 0.4, colors: ["#F07A3A", "#E8662E", "#F5A066"], n: 160, len: 26, width: 5, spread: 0.1 });
    c.fillStyle = "rgba(38,48,56,0.85)";
    [[0.3, 0.68, 46, 14], [0.58, 0.75, 34, 11], [0.72, 0.63, 24, 8]].forEach(([bx, by, bw, bh]) => {
      c.fillRect(W * bx - bw / 2, H * by, bw, bh); c.fillRect(W * bx - 4, H * by - bh * 1.4, 6, bh * 1.4);
    });
  }],
  // Monet — Femme à l'ombrelle
  ombrelle: [460, 600, (c, r, W, H) => {
    c.fillStyle = "#9CC3DA"; c.fillRect(0, 0, W, H);
    dabs(c, r, { x: 0, y: 0, w: W, h: H * 0.7, colors: ["#A9CDE2", "#D9E8EE", "#F4F1E6", "#8DB6D1", "#C4DCE7"], n: 2200, len: 24, width: 8, angle: -0.6, spread: 0.8 });
    dabs(c, r, { x: 0, y: H * 0.7, w: W, h: H * 0.3, colors: ["#7FA04E", "#A8B85A", "#5E7F3A", "#D8C86A", "#C9A5C8"], n: 1600, len: 16, width: 6, angle: -1.2, spread: 0.9 });
    dabs(c, r, { x: W * 0.42, y: H * 0.36, w: W * 0.18, h: H * 0.42, colors: ["#F4F1E6", "#E2E6EA", "#C8D3DB"], n: 420, len: 18, width: 6, angle: 1.4, spread: 0.5 });
    c.fillStyle = "#5E8A5A"; c.beginPath(); c.ellipse(W * 0.5, H * 0.3, 80, 30, -0.25, Math.PI, 0); c.fill();
    dabs(c, r, { x: W * 0.36, y: H * 0.22, w: W * 0.3, h: H * 0.08, colors: ["#6F9A62", "#4F7A4C", "#A7C08A"], n: 140, len: 14, width: 5 });
    c.strokeStyle = "#3D2415"; c.lineWidth = 2; c.beginPath(); c.moveTo(W * 0.5, H * 0.29); c.lineTo(W * 0.53, H * 0.45); c.stroke();
  }],
  // Monet — Nymphéas
  nympheas: [460, 460, (c, r, W, H) => {
    c.fillStyle = "#3E6B78"; c.fillRect(0, 0, W, H);
    dabs(c, r, { x: 0, y: 0, w: W, h: H, colors: ["#4C7F8C", "#2F5A66", "#6E9AA0", "#A3B9C9", "#7E8DB8"], n: 2400, len: 26, width: 6, spread: 0.2 });
    for (let i = 0; i < 16; i += 1) {
      const x = r() * W; const y = r() * H;
      dabs(c, r, { x: x - 30, y: y - 10, w: 60, h: 22, colors: ["#5E8A4A", "#7FA05A", "#3F6A3A"], n: 60, len: 12, width: 6 });
      if (r() > 0.4) dabs(c, r, { x: x - 6, y: y - 6, w: 12, h: 8, colors: ["#F2B8C6", "#F6E6EA", "#E98BA6"], n: 18, len: 6, width: 5 });
    }
  }],
  perfectdays: [500, 720, (c, r, W, H) => {
    c.fillStyle = "#24463D"; c.fillRect(0, 0, W, H);
    for (let i = 0; i < 260; i += 1) {
      c.fillStyle = `rgba(${200 + r() * 55},${220 + r() * 35},${120 + r() * 60},${0.05 + r() * 0.22})`;
      c.beginPath(); c.arc(r() * W, r() * H * 0.75, 4 + r() * 22, 0, Math.PI * 2); c.fill();
    }
    c.strokeStyle = "rgba(14,26,22,0.9)"; c.lineCap = "round";
    const branch = (x, y, a, l, w, d) => {
      if (d === 0) return;
      const x2 = x + Math.cos(a) * l; const y2 = y + Math.sin(a) * l;
      c.lineWidth = w; c.beginPath(); c.moveTo(x, y); c.lineTo(x2, y2); c.stroke();
      branch(x2, y2, a - 0.45 - r() * 0.2, l * 0.72, w * 0.7, d - 1);
      branch(x2, y2, a + 0.4 + r() * 0.2, l * 0.7, w * 0.7, d - 1);
    };
    branch(W * 0.5, H * 0.78, -Math.PI / 2, 120, 12, 8);
    c.fillStyle = "#1B6FA8"; c.fillRect(W * 0.12, H * 0.82, 70, 34); c.fillRect(W * 0.12 + 50, H * 0.8, 24, 14);
    c.fillStyle = "#111"; c.beginPath(); c.arc(W * 0.12 + 14, H * 0.82 + 36, 7, 0, 7); c.arc(W * 0.12 + 58, H * 0.82 + 36, 7, 0, 7); c.fill();
    title(c, "PERFECT DAYS", W / 2, H * 0.9, 42, "#F4EEDC");
    title(c, "UN FILM DE WIM WENDERS", W / 2, H * 0.955, 15, "#C8D9B0");
  }],
  blood: [500, 720, (c, r, W, H) => {
    c.fillStyle = "#E7D9BC"; c.fillRect(0, 0, W, H);
    for (let i = 0; i < 3000; i += 1) { c.fillStyle = `rgba(120,90,50,${r() * 0.08})`; c.fillRect(r() * W, r() * H, 2, 2); }
    c.fillStyle = "#C2461E"; c.beginPath(); c.arc(W * 0.5, H * 0.38, 120, 0, Math.PI * 2); c.fill();
    c.strokeStyle = "#151210"; c.lineWidth = 6;
    const bx = W * 0.5; const top = H * 0.14; const base = H * 0.68;
    c.beginPath(); c.moveTo(bx - 14, top); c.lineTo(bx - 90, base); c.moveTo(bx + 14, top); c.lineTo(bx + 90, base); c.stroke();
    c.lineWidth = 3;
    for (let k = 0; k < 9; k += 1) {
      const t1 = k / 9; const t2 = (k + 1) / 9;
      const y1 = top + (base - top) * t1; const y2 = top + (base - top) * t2;
      const w1 = 14 + 76 * t1; const w2 = 14 + 76 * t2;
      c.beginPath(); c.moveTo(bx - w1, y1); c.lineTo(bx + w2, y2); c.moveTo(bx + w1, y1); c.lineTo(bx - w2, y2); c.moveTo(bx - w2, y2); c.lineTo(bx + w2, y2); c.stroke();
    }
    c.fillStyle = "#151210"; c.fillRect(0, base, W, H - base);
    c.beginPath(); c.moveTo(bx, top - 10);
    for (let i = 0; i < 30; i += 1) c.lineTo(bx + (r() - 0.5) * 60 * (i / 30), top - 10 - i * 4);
    c.lineWidth = 10; c.strokeStyle = "rgba(21,18,16,0.85)"; c.stroke();
    title(c, "THERE WILL", W / 2, H * 0.78, 46, "#E7D9BC", "700 {s}px Georgia, serif", 0.12);
    title(c, "BE BLOOD", W / 2, H * 0.85, 46, "#C2461E", "700 {s}px Georgia, serif", 0.12);
    title(c, "PAUL THOMAS ANDERSON", W / 2, H * 0.93, 15, "#E7D9BC");
  }],
  revenant: [760, 440, (c, r, W, H) => {
    const g = c.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, "#B9C8D2"); g.addColorStop(0.6, "#E4EAEE"); g.addColorStop(1, "#F5F7F8");
    c.fillStyle = g; c.fillRect(0, 0, W, H);
    c.fillStyle = "#8EA1AE"; c.beginPath(); c.moveTo(0, H * 0.55);
    [[0.15, 0.3], [0.3, 0.45], [0.45, 0.22], [0.62, 0.42], [0.8, 0.28], [1, 0.5]].forEach(([x, y]) => c.lineTo(W * x, H * y));
    c.lineTo(W, H); c.lineTo(0, H); c.fill();
    c.fillStyle = "#EEF2F4"; c.fillRect(0, H * 0.62, W, H);
    for (let i = 0; i < 26; i += 1) {
      const x = r() * W; const h = 40 + r() * 90; const y = H * 0.66 - r() * 10;
      c.fillStyle = `rgba(28,38,40,${0.7 + r() * 0.3})`;
      c.beginPath(); c.moveTo(x, y - h); c.lineTo(x - h * 0.22, y); c.lineTo(x + h * 0.22, y); c.fill();
    }
    c.fillStyle = "#1E2224"; c.fillRect(W * 0.47, H * 0.7, 8, 22); c.beginPath(); c.arc(W * 0.474, H * 0.69, 7, 0, 7); c.fill();
    for (let i = 0; i < 500; i += 1) { c.fillStyle = "rgba(255,255,255,0.8)"; c.fillRect(r() * W, r() * H, 2, 2); }
    title(c, "THE REVENANT", W / 2, H * 0.86, 44, "#2B3236", "300 {s}px Helvetica, Arial, sans-serif", 0.4);
    title(c, "ALEJANDRO G. IÑÁRRITU", W / 2, H * 0.94, 14, "#4A555B");
  }],
  wanted: [420, 600, (c, r, W, H) => {
    c.fillStyle = "#E6CF9C"; c.fillRect(0, 0, W, H);
    for (let i = 0; i < 2500; i += 1) { c.fillStyle = `rgba(110,70,30,${r() * 0.1})`; c.fillRect(r() * W, r() * H, 3, 3); }
    title(c, "WANTED", W / 2, H * 0.1, 78, "#3B2414", "900 {s}px Georgia, serif", 0.05);
    c.fillStyle = "#F1E2BE"; c.fillRect(W * 0.1, H * 0.18, W * 0.8, H * 0.5);
    c.strokeStyle = "#3B2414"; c.lineWidth = 4; c.strokeRect(W * 0.1, H * 0.18, W * 0.8, H * 0.5);
    c.fillStyle = "#E3B53C"; c.beginPath(); c.ellipse(W / 2, H * 0.47, 130, 38, 0, 0, Math.PI * 2); c.fill();
    c.beginPath(); c.ellipse(W / 2, H * 0.4, 70, 60, 0, Math.PI, 0); c.fill();
    c.fillStyle = "#C0262B"; c.fillRect(W / 2 - 70, H * 0.4 - 6, 140, 18);
    title(c, "DEAD OR ALIVE", W / 2, H * 0.75, 30, "#3B2414", "700 {s}px Georgia, serif", 0.1);
    title(c, "MONKEY·D·LUFFY", W / 2, H * 0.83, 34, "#3B2414", "900 {s}px Georgia, serif", 0.04);
    title(c, "฿ 3.000.000.000-", W / 2, H * 0.92, 32, "#3B2414", "700 {s}px Georgia, serif", 0.04);
  }],
  frieren: [400, 560, (c, r, W, H) => {
    const g = c.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, "#DCE6F2"); g.addColorStop(1, "#F6F2EA");
    c.fillStyle = g; c.fillRect(0, 0, W, H);
    for (let i = 0; i < 700; i += 1) {
      c.fillStyle = r() > 0.3 ? "#6F9BD6" : "#F4F1FA";
      c.beginPath(); c.arc(r() * W, H * 0.62 + r() * H * 0.38, 2 + r() * 4, 0, 7); c.fill();
    }
    c.strokeStyle = "#E7E2D6"; c.lineWidth = 8; c.beginPath(); c.moveTo(W * 0.62, H * 0.15); c.lineTo(W * 0.48, H * 0.82); c.stroke();
    c.strokeStyle = "#C9A54A"; c.lineWidth = 6; c.beginPath(); c.arc(W * 0.63, H * 0.15, 26, 0.6, Math.PI * 1.7); c.stroke();
    c.fillStyle = "#C2262E"; c.beginPath(); c.arc(W * 0.63, H * 0.15, 11, 0, 7); c.fill();
    title(c, "FRIEREN", W / 2, H * 0.08, 40, "#3E4A66", "400 {s}px Georgia, serif", 0.3);
    title(c, "AU-DELÀ DE L'AVENTURE", W / 2, H * 0.94, 14, "#3E4A66");
  }],
  jjk: [400, 560, (c, r, W, H) => {
    c.fillStyle = "#0F1020"; c.fillRect(0, 0, W, H);
    const orb = (x, y, col) => {
      const g = c.createRadialGradient(x, y, 0, x, y, 120);
      g.addColorStop(0, "#FFFFFF"); g.addColorStop(0.2, col); g.addColorStop(1, "rgba(0,0,0,0)");
      c.fillStyle = g; c.beginPath(); c.arc(x, y, 120, 0, 7); c.fill();
    };
    c.globalCompositeOperation = "lighter";
    orb(W * 0.38, H * 0.42, "rgba(60,110,255,0.9)");
    orb(W * 0.62, H * 0.42, "rgba(255,50,70,0.9)");
    orb(W * 0.5, H * 0.42, "rgba(150,60,255,0.6)");
    c.globalCompositeOperation = "source-over";
    title(c, "JUJUTSU", W / 2, H * 0.8, 48, "#F2F2F7", "900 {s}px Helvetica, Arial, sans-serif", 0.08);
    title(c, "KAISEN", W / 2, H * 0.89, 48, "#F2F2F7", "900 {s}px Helvetica, Arial, sans-serif", 0.08);
  }],
};

export const getArt = (name) => {
  if (!ART[name]) return null;
  if (!cache.has(name)) {
    const [W, H, draw] = ART[name];
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    draw(canvas.getContext("2d"), rng(name.length * 977 + 31), W, H);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 8;
    cache.set(name, tex);
  }
  return cache.get(name);
};
